import { forwardRef, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type InputHTMLAttributes, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '@/lib/cx';
import { controlBase, controlTone } from './control';
import { Icon } from './Icon';
import { blockArrowSpin, blockWheelSpin } from './numberGuard';
import { Spinner } from './Spinner';
import { type Tone } from './status';

/**
 * Form controls.
 *
 * Every field is 32px tall, rounded `md`, ruled not shadowed, and shows its
 * error in the same place with the same treatment. In v1 each screen invented
 * this again, which is why a required-field error looked different on the
 * student form than on the fee form.
 *
 * ── Error copy ──
 * Errors say what happened and how to fix it. "Enter the amount collected"
 * not "Invalid input"; "This enrolment number is already in use" not "Error".
 */

/* The control's height, radius, ruling and focus tone live in ./control.ts —
   see the note there on why they are not declared in this file. */

/* ── Field shell: label, control, error, hint ───────────────────────────── */

interface FieldShellProps {
  label?: React.ReactNode;
  htmlFor?: string;
  required?: boolean;
  error?: string;
  /** Shown only while there is no error — the two never stack. */
  hint?: string;
  className?: string;
  children: React.ReactNode;
}

export function FieldShell({ label, htmlFor, required, error, hint, className, children }: FieldShellProps) {
  return (
    <div className={cx('flex flex-col gap-1', className)}>
      {label && (
        <label
          htmlFor={htmlFor}
          className="text-2xs font-medium uppercase tracking-wide text-muted"
        >
          {label}
          {required && <span className="ml-0.5 text-danger">*</span>}
        </label>
      )}
      {children}
      {error ? (
        <span className="flex items-center gap-1 text-2xs text-danger">
          <Icon name="alert" size={11} />
          {error}
        </span>
      ) : hint ? (
        <span className="text-2xs text-muted">{hint}</span>
      ) : null}
    </div>
  );
}

/* ── Text ───────────────────────────────────────────────────────────────── */

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'size'> {
  label?: React.ReactNode;
  error?: string;
  hint?: string;
  /** Leading icon inside the control — search fields, mostly. */
  icon?: 'search';
  className?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, hint, icon, className, required, id, ...rest },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <FieldShell label={label} htmlFor={fieldId} required={required} error={error} hint={hint} className={className}>
      <div className="relative">
        {icon && (
          <Icon
            name={icon}
            size={14}
            className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-faint"
          />
        )}
        <input
          ref={ref}
          id={fieldId}
          required={required}
          aria-invalid={error ? true : undefined}
          className={cx(controlBase, controlTone(!!error), icon && 'pl-7')}
          {...rest}
        />
      </div>
    </FieldShell>
  );
});

/* ── Multi-line text ────────────────────────────────────────────────────────
   For the one or two fields in the app that are genuinely prose — homework
   instructions, a notice body. Everything else is a `TextField`.

   It cannot share `controlBase`: that fixes a 32px height, which is the whole
   point of a control that sits on one line and exactly wrong for one that does
   not. The ruling, radius, padding and focus tone are the same, so the two read
   as the same family in a form grid — only the height rule differs.

   `rows` sets the resting height and the box grows no further on its own: a
   textarea that resizes as you type reflows every field under it mid-sentence.
   Vertical drag is left on so a long instruction can still be seen whole. */

interface TextAreaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className' | 'rows'> {
  label?: React.ReactNode;
  error?: string;
  hint?: string;
  /** Resting height in lines. 3 is the form default. */
  rows?: number;
  className?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, error, hint, rows = 3, className, required, id, readOnly, ...rest },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <FieldShell label={label} htmlFor={fieldId} required={required} error={error} hint={hint} className={className}>
      <textarea
        ref={ref}
        id={fieldId}
        rows={rows}
        required={required}
        readOnly={readOnly}
        aria-invalid={error ? true : undefined}
        className={cx(
          'w-full rounded-md border px-2 py-1.5 text-sm leading-normal',
          'transition-colors duration-100',
          'placeholder:text-faint',
          'disabled:cursor-not-allowed disabled:bg-subtle disabled:text-faint',
          /* Read-only has to *look* read-only. A box with a white fill, a live
             hover rule and a resize grip is an invitation to type, and a field
             that accepts a caret and then silently discards the edit is the
             fake-affordance failure this rewrite exists to remove. It is a
             distinct state from `disabled`: the text is being shown *because it
             matters*, so it stays selectable and legible rather than greyed to
             `text-faint`. Used by the task form's completion note, which the
             backend owns. */
          readOnly
            ? 'resize-none cursor-default border-line bg-subtle text-muted'
            : cx('resize-y bg-surface text-ink', controlTone(!!error)),
        )}
        {...rest}
      />
    </FieldShell>
  );
});

/* ── Number / amount ────────────────────────────────────────────────────── */

interface NumberFieldProps extends Omit<TextFieldProps, 'icon' | 'type'> {
  /** Prefixes the control with ₹ and switches to a larger figure treatment. */
  currency?: boolean;
  /** Bigger input for the one amount a screen is built around. */
  emphasis?: boolean;
}

export const NumberField = forwardRef<HTMLInputElement, NumberFieldProps>(function NumberField(
  { label, error, hint, currency, emphasis, className, required, id, onKeyDown, ...rest },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <FieldShell label={label} htmlFor={fieldId} required={required} error={error} hint={hint} className={className}>
      <div className="relative">
        {currency && (
          <span
            className={cx(
              'pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-faint',
              emphasis && 'text-lg',
            )}
          >
            ₹
          </span>
        )}
        <input
          ref={ref}
          id={fieldId}
          type="number"
          inputMode="decimal"
          required={required}
          aria-invalid={error ? true : undefined}
          // An amount must not change because someone scrolled or arrowed past
          // it. See numberGuard.
          onWheel={blockWheelSpin}
          onKeyDown={(e) => {
            onKeyDown?.(e);
            if (!e.defaultPrevented) blockArrowSpin(e);
          }}
          className={cx(
            controlBase,
            controlTone(!!error),
            'text-right tabular-nums',
            // Spinners steal horizontal space and are useless for money.
            '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
            currency && (emphasis ? 'pl-7' : 'pl-6'),
            emphasis && 'h-12 text-2xl font-semibold',
          )}
          {...rest}
        />
      </div>
    </FieldShell>
  );
});

/* ── Native select ──────────────────────────────────────────────────────── */

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> {
  label?: React.ReactNode;
  error?: string;
  hint?: string;
  /** Rendered as the first, value-less option. */
  placeholder?: string;
  /**
   * `disabled` shows an option that cannot be chosen — a fee head already on
   * the structure, say. Prefer it over filtering the option out when its
   * absence would read as "this does not exist" rather than "not this one".
   */
  options: { value: string; label: string; disabled?: boolean }[];
  className?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, error, hint, placeholder, options, className, required, id, ...rest },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <FieldShell label={label} htmlFor={fieldId} required={required} error={error} hint={hint} className={className}>
      <div className="relative">
        <select
          ref={ref}
          id={fieldId}
          required={required}
          aria-invalid={error ? true : undefined}
          className={cx(controlBase, controlTone(!!error), 'cursor-pointer appearance-none pr-7')}
          {...rest}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </select>
        <Icon
          name="chevronDown"
          size={14}
          className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-faint"
        />
      </div>
    </FieldShell>
  );
});

/* ── Searchable select (combobox) ───────────────────────────────────────────
   v1 implemented this twice, in GatePass.tsx and TaskManagement.tsx, with
   different keyboard behaviour in each. It is the canonical example of why
   this directory exists. */

export interface ComboOption {
  value: string;
  label: string;
  /** Second line in the option row — enrolment number, class, phone. */
  meta?: string;
}

/** The list's ceiling, and the breathing room kept against the viewport edge. */
const LIST_MAX_H = 256;
const LIST_GUTTER = 8;

interface SearchableSelectProps {
  label?: React.ReactNode;
  value: string | null;
  onChange: (value: string | null) => void;
  options: ComboOption[];
  placeholder?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  /** Called as the user types, for server-side search. Debounce upstream. */
  onSearch?: (query: string) => void;
  className?: string;
}

export function SearchableSelect({
  label,
  value,
  onChange,
  options,
  placeholder = 'Search…',
  error,
  hint,
  required,
  disabled,
  loading,
  onSearch,
  className,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const anchorRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [pin, setPin] = useState<{
    x: number;
    y: number;
    w: number;
    h: number;
    /** Opens upward, because there is more room above than below. */
    up: boolean;
  } | null>(null);
  const fieldId = useId();

  const selected = useMemo(() => options.find((o) => o.value === value) ?? null, [options, value]);

  // Local filtering when the caller has not taken over search.
  const visible = useMemo(() => {
    if (onSearch || !query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q) || o.meta?.toLowerCase().includes(q));
  }, [options, query, onSearch]);

  /* ── Placement ────────────────────────────────────────────────────────────
     The list is portalled to <body>, for the reason DateField's calendar is.
     Left in the flow it is an absolutely positioned child, and every container
     this field is used in clips one: `<FormSection>` is `overflow-hidden` to
     clip its own rounded corners, `<Drawer>`'s body and `<Pane>`'s scroller are
     `overflow-auto`. On the task form the assignee picker is the last field in
     its section, so the staff list opened *inside* that clip and was simply not
     on screen — the box looked broken rather than empty.

     Escaping the clip means positioning from a measured rect, and re-measuring
     whenever anything moves underneath it. */
  const place = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const below = window.innerHeight - rect.bottom - LIST_GUTTER - 4;
    const above = rect.top - LIST_GUTTER - 4;
    /* Flip up only when dropping down is the worse of the two. A list that
       changes direction on a field with room for it is disorienting; one that
       opens into 30px of viewport is unusable. */
    const up = below < Math.min(LIST_MAX_H, above);
    const next = {
      x: rect.left,
      // Placed by its top edge either way — `-translate-y-full` lifts it by its
      // own height when it opens upward, so a short list still sits against the
      // box instead of floating a full panel-height above it.
      y: up ? rect.top - 4 : rect.bottom + 4,
      w: rect.width,
      h: Math.min(LIST_MAX_H, up ? above : below),
      up,
    };
    /* Scrolling the list itself fires the capture listener below. Bailing when
       nothing moved keeps a 200-name staff list from re-rendering on every
       wheel tick. */
    setPin((prev) =>
      prev && prev.x === next.x && prev.y === next.y && prev.w === next.w && prev.h === next.h && prev.up === next.up
        ? prev
        : next,
    );
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    window.addEventListener('resize', place);
    // Capture phase: the scroll happens on some ancestor, not on window.
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  /* Dismiss on a click outside either the box or the list — they are in
     different trees now, so both have to be checked. Missing the second one
     would unmount the list on pointerdown and swallow the click that chose an
     option. */
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!anchorRef.current?.contains(t) && !listRef.current?.contains(t)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const commit = (option: ComboOption) => {
    onChange(option.value);
    setQuery('');
    setOpen(false);
  };

  const list = open && pin && (
    <ul
      ref={listRef}
      id={`${fieldId}-list`}
      role="listbox"
      data-print="hide"
      className={cx(
        'pinned-anchored z-50 overflow-auto rounded-md border border-line bg-surface py-1 shadow-md',
        pin.up && '-translate-y-full',
      )}
      /* eslint-disable-next-line no-restricted-syntax -- the box's position and
         width on screen, and the room left beside it, measured at open time and
         again on every scroll. None of it can come from a token: it is different
         at every call site and changes as the page moves. The `pinned-anchored`
         utility in tokens.css consumes these four; no design value is set here. */
      style={
        {
          '--pin-x': `${pin.x}px`,
          '--pin-y': `${pin.y}px`,
          '--pin-w': `${pin.w}px`,
          '--pin-h': `${pin.h}px`,
        } as React.CSSProperties
      }
    >
      {visible.length === 0 ? (
        <li className="px-2 py-2 text-xs text-muted">{loading ? 'Searching…' : 'No matches'}</li>
      ) : (
        visible.map((o, i) => (
          <li
            key={o.value}
            role="option"
            aria-selected={o.value === value}
            onPointerEnter={() => setActive(i)}
            onClick={() => commit(o)}
            className={cx('cursor-pointer px-2 py-1.5', i === active ? 'bg-accent-soft' : 'bg-surface')}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-ink">{o.label}</span>
              {o.value === value && <Icon name="check" size={13} className="text-accent" />}
            </div>
            {o.meta && <div className="font-mono text-2xs text-muted">{o.meta}</div>}
          </li>
        ))
      )}
    </ul>
  );

  return (
    <FieldShell label={label} htmlFor={fieldId} required={required} error={error} hint={hint} className={className}>
      {/* One wrapper, and it is the anchor: its rect is the box the list is
          placed against, so it must be exactly the input's box. */}
      <div ref={anchorRef} className="relative">
        <input
          id={fieldId}
          role="combobox"
            aria-expanded={open}
          aria-controls={`${fieldId}-list`}
          aria-autocomplete="list"
          autoComplete="off"
          disabled={disabled}
          value={open ? query : (selected?.label ?? '')}
          placeholder={selected ? selected.label : placeholder}
          aria-invalid={error ? true : undefined}
          onFocus={() => setOpen(true)}
          /* Focus alone is not enough to reopen: after Escape the box still has
             it, so a second click fires no focus event and the field would sit
             there looking dead. */
          onClick={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
            setOpen(true);
            onSearch?.(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setOpen(true);
              setActive((i) => Math.min(i + 1, visible.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter' && open && visible[active]) {
              e.preventDefault();
              commit(visible[active]);
            } else if (e.key === 'Escape' && open) {
              /* Escape closes the list, and only the list. Without this the
                 key reaches <Drawer>'s document listener and shuts the whole
                 form — losing everything typed into it — when all the user
                 meant was to dismiss a dropdown they had opened. */
              e.stopPropagation();
              setOpen(false);
              setQuery('');
            }
          }}
          className={cx(controlBase, controlTone(!!error), 'pr-7')}
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          {loading ? (
            <Spinner size={13} className="text-faint" />
          ) : value && !disabled ? (
            <button
              type="button"
              aria-label="Clear selection"
              onClick={() => {
                onChange(null);
                setQuery('');
              }}
              className="text-faint hover:text-ink"
            >
              <Icon name="close" size={13} />
            </button>
          ) : (
            <Icon name="chevronDown" size={14} className="pointer-events-none text-faint" />
          )}
        </div>
      </div>
      {list && createPortal(list, document.body)}
    </FieldShell>
  );
}

/* ── Toggle ─────────────────────────────────────────────────────────────────
   For a genuine boolean. A two-option <select> costs three interactions —
   open, read, choose — to express one bit, and reads as though there might be
   a third option. A switch is one click and its state is legible without being
   opened.

   Use it only where the two states are opposites of the same thing (teaching /
   non-teaching, active / inactive). Where the options are peers rather than
   on/off, use <Segmented>. */

interface ToggleProps {
  label?: React.ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Word shown beside the switch. Name the state, not the action. */
  onLabel?: string;
  offLabel?: string;
  hint?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
}

export function Toggle({
  label,
  checked,
  onChange,
  onLabel = 'Yes',
  offLabel = 'No',
  hint,
  error,
  disabled,
  className,
}: ToggleProps) {
  const id = useId();
  return (
    <FieldShell label={label} htmlFor={id} error={error} hint={hint} className={className}>
      {/* Same 32px box as every other control, so a toggle sitting beside a
          text field in a form grid lines up with it. */}
      <div className="flex h-8 items-center gap-2">
        <button
          id={id}
          type="button"
          role="switch"
          aria-checked={checked}
          disabled={disabled}
          onClick={() => onChange(!checked)}
          className={cx(
            'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-150',
            'disabled:cursor-not-allowed disabled:opacity-50',
            checked ? 'bg-accent' : 'bg-line-strong',
          )}
        >
          <span
            className={cx(
              'inline-block size-4 rounded-full bg-surface shadow-sm transition-transform duration-150',
              checked ? 'translate-x-4.5' : 'translate-x-0.5',
            )}
          />
        </button>
        <span className={cx('text-sm', checked ? 'text-ink' : 'text-muted')}>
          {checked ? onLabel : offLabel}
        </span>
      </div>
    </FieldShell>
  );
}

/* ── Checkbox ────────────────────────────────────────────────────────────────
   A tick, for a list of things being included rather than a single yes/no —
   which is what `Toggle` is for. The collection desk's fee catalog is the case
   this exists for: a dozen rows the collector ticks on and off, where a dozen
   switches would read as a dozen independent settings.

   The whole row is the label, so the hit target is the text and not just the
   16px box. */

interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: React.ReactNode;
  /** Secondary text after the label — a reason, an amount, a note. */
  hint?: React.ReactNode;
  disabled?: boolean;
  className?: string;
}

export function Checkbox({ checked, onChange, label, hint, disabled, className }: CheckboxProps) {
  const id = useId();
  return (
    <label
      htmlFor={id}
      className={cx(
        'flex items-center gap-2',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        className,
      )}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className={cx(
          'size-4 shrink-0 rounded-sm border border-line-strong bg-surface',
          'accent-accent disabled:cursor-not-allowed',
        )}
      />
      {(label || hint) && (
        <span className="min-w-0 flex-1 text-sm text-ink">
          {label}
          {hint && <span className="ml-1.5 text-xs text-muted">{hint}</span>}
        </span>
      )}
    </label>
  );
}

/* ── Segmented control ──────────────────────────────────────────────────────
   Used where v1 reached for a <select> on a short, known list — payment
   method, attendance status, density. Every option visible means one click
   instead of two, which matters at a fee counter with a queue. */

/* The selected segment, when the option carries a tone. Same pairs `<Badge>`
   uses, so a status is the same colour whether it is being read in a table or
   chosen in a grid — which is the point of having one tone vocabulary. */
const segmentTones: Record<Tone, string> = {
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
  info: 'bg-info-soft text-info',
  neutral: 'bg-neutral-soft text-neutral',
  accent: 'bg-accent-soft text-accent',
};

interface SegmentedProps<T extends string> {
  label?: React.ReactNode;
  value: T;
  onChange: (value: T) => void;
  /**
   * `tone` colours the segment while it is the selected one. Use it where the
   * choice *is* a status — attendance is the case this exists for, per
   * docs/domain/attendance-ui-directives.md §1, which asks for present to read
   * green and absent red at a glance down a column of forty rows.
   */
  options: { value: T; label: string; tone?: Tone; title?: string }[];
  /** `sm` fits inside a table cell; `md` (default) sits in a form. */
  size?: 'sm' | 'md';
  disabled?: boolean;
  className?: string;
}

export function Segmented<T extends string>({
  label,
  value,
  onChange,
  options,
  size = 'md',
  disabled,
  className,
}: SegmentedProps<T>) {
  return (
    <FieldShell label={label} className={className}>
      <div
        role="radiogroup"
        className={cx(
          'inline-flex rounded-md border border-line-strong bg-subtle p-0.5',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        {options.map((o) => {
          const selected = value === o.value;
          return (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={selected}
              title={o.title}
              disabled={disabled}
              onClick={() => onChange(o.value)}
              className={cx(
                'rounded-sm font-medium transition-colors duration-100',
                size === 'sm' ? 'h-5 min-w-6 px-1.5 text-2xs' : 'h-6 px-2.5 text-xs',
                'disabled:cursor-not-allowed',
                selected
                  ? cx('shadow-md', o.tone ? segmentTones[o.tone] : 'bg-surface text-ink')
                  : 'bg-transparent text-muted hover:text-ink',
              )}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </FieldShell>
  );
}
